import React from "react";
import { getCarBrandLabel } from "../../../constants/carBrands";
import { getCarEffortColor } from "../../../utils/appointmentUtils";
import { CarBrandLogo } from "../CarBrandLogo";
import { MercosulPlateBadge } from "../MercosulPlateBadge";

export interface ClientCarCardCar {
  model: string;
  plate: string;
  brand?: string | null;
  effort?: string | null;
}

interface ClientCarCardProps {
  car: ClientCarCardCar;
  size?: "sm" | "md";
  clientName?: string;
  serviceName?: string;
  /** Status or helper text shown inside the card */
  message?: React.ReactNode;
  /** Action buttons shown at the bottom of the card */
  actions?: React.ReactNode;
  /** Content aligned with the plate, e.g. registration date */
  meta?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

function getEffortAccent(effort?: string | null): string {
  switch (effort) {
    case "Leve":
      return "border-l-emerald-500 dark:!border-l-emerald-400";
    case "Médio":
      return "border-l-amber-500 dark:!border-l-amber-400";
    case "Pesado":
      return "border-l-rose-500 dark:!border-l-rose-400";
    default:
      return "border-l-slate-300 dark:!border-l-slate-500";
  }
}

export const ClientCarCard: React.FC<ClientCarCardProps> = ({
  car,
  size = "md",
  clientName,
  serviceName,
  message,
  actions,
  meta,
  children,
  className = "",
  onClick,
}) => {
  const compact = size === "sm";
  const brandLabel = getCarBrandLabel(car.brand);
  const footerActions = actions ?? children;

  return (
    <article
      onClick={onClick}
      className={`group bg-white dark:bg-slate-800 rounded-2xl border-y border-r border-gray-100 dark:border-slate-700 border-l-4 shadow-sm overflow-hidden text-left hover:shadow-md hover:border-y-gray-200 hover:border-r-gray-200 dark:hover:border-y-slate-500 dark:hover:border-r-slate-500 transition-all duration-200 ${getEffortAccent(
        car.effort,
      )} ${onClick ? "cursor-pointer" : ""} ${className}`}
    >
      <div className={compact ? "p-3" : "p-4 sm:p-5"}>
        {clientName ? (
          <p
            className={`font-semibold text-gray-900 dark:text-white leading-snug break-words ${
              compact ? "text-sm mb-2" : "text-base sm:text-lg mb-3"
            }`}
          >
            {clientName}
          </p>
        ) : null}

        <div className="flex items-start gap-3">
          <div
            className={`rounded-2xl bg-slate-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-600 shadow-sm flex items-center justify-center shrink-0 ${
              compact ? "w-11 h-11" : "w-14 h-14"
            }`}
          >
            <CarBrandLogo
              brand={car.brand}
              size={compact ? 32 : 40}
              fallbackClassName="bg-gradient-to-br from-sky-500 to-blue-600"
            />
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className={`font-semibold text-gray-900 dark:text-white leading-snug break-words ${
                compact ? "text-sm" : "text-base sm:text-lg"
              }`}
            >
              {car.model}
            </h3>
            <p
              className={`mt-0.5 text-gray-500 truncate ${
                compact ? "text-xs" : "text-xs sm:text-sm"
              }`}
            >
              {brandLabel || "Marca não informada"}
            </p>
          </div>
          {car.effort ? (
            <span
              className={`shrink-0 inline-flex items-center rounded-lg font-semibold border ${getCarEffortColor(
                car.effort,
              )} ${compact ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"}`}
            >
              {car.effort}
            </span>
          ) : null}
        </div>

        {serviceName ? (
          <p
            className={`text-gray-600 ${
              compact ? "mt-2 text-xs" : "mt-3 text-sm"
            }`}
          >
            {serviceName}
          </p>
        ) : null}

        <div
          className={`flex flex-wrap items-end justify-between gap-2 sm:gap-3 ${
            compact ? "mt-3" : "mt-4"
          }`}
        >
          <MercosulPlateBadge plate={car.plate} size={size} />
          {meta ? (
            <div className="text-[11px] sm:text-xs text-gray-400 text-right leading-tight">
              {meta}
            </div>
          ) : null}
        </div>

        {(message || footerActions) ? (
          <div
            className={`flex items-center gap-2 ${
              message ? "justify-between" : "justify-end"
            } ${compact ? "mt-2" : "mt-3"}`}
            onClick={(event) => event.stopPropagation()}
          >
            {message ? (
              <div
                className={`min-w-0 flex-1 text-gray-600 ${
                  compact ? "text-xs" : "text-sm"
                }`}
              >
                {message}
              </div>
            ) : null}
            {footerActions ? (
              <div className="shrink-0">{footerActions}</div>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );
};

